<template>
  <div class="masking-module"></div>
</template>

<script>
import { mapState, mapGetters, mapMutations } from 'vuex';
import MaskingModule from '@renoworks/masking-module';

export default {
  name: 'masking',
  props: {
    interactive: Boolean,
  },
  data() {
    return {
      projectLoadedPromise: Promise.resolve(),
    };
  },
  computed: {
    ...mapState([
      'renderProject',
      'shellProject',
      'currentLayer',
    ]),
    ...mapGetters([
      'currentProject',
    ]),
  },
  watch: {
    async interactive(interactive) {
      // enable or disable mask interactivity
      if (interactive) {
        // project must be loaded before layers can be interacted with
        await this.projectLoadedPromise;
        this.interactWithLayers();

        await MaskingModule
          // make layers showable
          .setLayerViewOption(MaskingModule.LayerViewOption.all)
          .setHoverOption(MaskingModule.LayerHoverOption.highlight)
          // refresh
          .draw();
      } else {
        await MaskingModule
          // prevent layers from being showable
          .setLayerViewOption(MaskingModule.LayerViewOption.none)
          .setHoverOption(MaskingModule.LayerHoverOption.none)
          // hide tools
          .selectLayer(null)
          .removeTool()
          // refresh
          .draw();
      }
    },
    async currentProject(currentProject) {
      // @TODO: make this more sensitive to significant changes
      // Example: if the folder has changed, number of layers, ppf, or other
      if (currentProject) {
        // manually (re-)load project into MM, storing the promise to avoid races w/ interactivity
        this.projectLoadedPromise = this.projectLoadedPromise.then(async () => {
          const xml = currentProject.xml();
          const path = currentProject.getPath();
          await MaskingModule.loadProjectXML(xml, path, true);
        });
        await this.projectLoadedPromise;

        // turn on interactivity, if necessary
        if (this.interactive) {
          this.interactWithLayers();
        }

        // refresh viewport and project sizing
        await MaskingModule.view.updateDisplay();

        // reset MM viewport settings
        await MaskingModule
          .resetZoom()
          .panCenter()
          .draw();
      } else if (!currentProject) {
        // handle project unset
        await MaskingModule
          .reset()
          .draw();
      }
    },
    'currentProject.renderSrc': async function renderSrcWatcher(renderSrc) {
      if (renderSrc) {
        await this.projectLoadedPromise;
        await MaskingModule.setDisplayImage(renderSrc, true);
      }
    },
  },
  methods: {
    ...mapMutations([
      'setCurrentLayer',
    ]),
    getMaskingLayerByID(layerID) {
      return MaskingModule.getLayer(layerID);
    },
    getRenderLayerByID(layerID) {
      return this.renderProject.layers.find(layer => `${layer.id}` === `${layerID}`);
    },
    getShellLayerByID(layerID) {
      return this.shellProject.layers.find(layer => `${layer.id}` === `${layerID}`);
    },
    createBackdrop(layer) {
      // adds gutter, since layer polygons can have starts/ends outside of project boundaries
      const perspectivePolygon = layer.perspective.contour.add(MaskingModule.state.gutter);
      const backdropElement = new MaskingModule.PolygonElement(perspectivePolygon, true, 'RWDoor');

      // adds the visual element to the layer for display
      layer.backdropElement = layer.addElement(backdropElement);
    },
    removeBackdrop(layer) {
      if (layer.backdropElement) {
        layer.removeElement(layer.backdropElement);
        layer.backdropElement = null;
      }
    },
    // add clickable area (since openings don't show a clickable layer otherwise)
    showLayer(layerID) {
      const maskingLayer = this.getMaskingLayerByID(layerID);
      if (maskingLayer) {
        // remove existing backdrop element, if necessary
        this.removeBackdrop(maskingLayer);

        // create new backdrop element using layer perspective
        this.createBackdrop(maskingLayer);

        // save initial perspective into project
        this.saveLayer(layerID);
      } else {
        throw new Error(`no masking layer of id: ${layerID}`);
      }
    },
    // show custom opening tool on top of layer, thereby allowing the user to edit it
    editLayer(layerID) {
      try {
        const maskingLayer = this.getMaskingLayerByID(layerID);
        if (maskingLayer) {
          // remove existing backdrop element, if necessary
          this.removeBackdrop(maskingLayer);

          MaskingModule
            .selectLayer(layerID)
            .setTool(MaskingModule.Tools.Opening)
            // manually re-enable layer hovering since opening a tool disables it by default
            .setHoverOption(MaskingModule.LayerHoverOption.highlight);
        } else {
          throw new Error(`no masking layer of id: ${layerID}`);
        }
      } catch (err) {
        // there is no existing MM layer, so add layers
        if (err.message.indexOf('invalid layer') > -1) {
          throw new Error(`no masking layer of id: ${layerID}`);
        } else {
          throw (err); // pass on other errors
        }
      }
    },
    // update the layer's perspective points to match the current tool placement
    saveLayer(layerID) {
      const maskingLayer = this.getMaskingLayerByID(layerID);
      if (maskingLayer) {
        const renderLayer = this.getRenderLayerByID(layerID);
        const shellLayer = this.getShellLayerByID(layerID);
        [renderLayer, shellLayer].filter(Boolean).forEach((layer) => {
          // store in memory changes to points in the order the masking module uses them
          // mapping from MM models to CF/RE model happens on project save
          const { points } = maskingLayer.perspective.contour;
          if (layer.perspective) {
            layer.perspective.setPointsWithArrayOfObj(points);
          } else {
            const rwPoints = points.map(point => new window.Renoworks.Point(point.x, point.y));
            const height = rwPoints[3].y - rwPoints[0].y;
            const width = rwPoints.length < 6
              ? rwPoints[1].x - rwPoints[0].x
              : rwPoints[3].x - rwPoints[4].x;
            layer.perspective = new window.Renoworks.Perspective(
              ...rwPoints.slice(0, 4),
              width,
              height,
              ...rwPoints.slice(4),
            );
          }
        });
      } else {
        throw new Error(`no layer of id: ${layerID}`);
      }
    },
    interactWithLayers() {
      // add visible/hoverable/clickable layer masks for all layers
      MaskingModule.state.layerIDs.forEach((layerID) => {
        this.showLayer(layerID);
      });

      // automatically start editing the currentLayer
      if (this.currentLayer) {
        this.editLayer(this.currentLayer.id);
      }
    },
  },
  mounted() {
    MaskingModule.Settings.set({
      gutter: 1,
      loadVectorOnly: true, // CHI does not have raster layers, optimize project loading
      layers: {
        globalMaskColor: 'rgba(255, 255, 255, 0.33)',
        globalMaskStrokeColor: '#D0021B',
        polygonAttrs: {
          cursor: 'pointer',
        },
      },
      view: {
        selectedOpacity: 1,
        highlightedOpacity: 1,
        nonHighlightedOpacity: 1,
      },
      tools: {
        opening: {
          polygon: {
            fill: 'rgba(255, 255, 255, 0.66)',
            'fill-opacity': 1,
          },
          edge: {
            stroke: '#D0021B',
          },
          handle: {
            fill: '#D0021B',
            relative: {
              r: 5,
            },
          },
        },
      },
    });

    MaskingModule.view.bind({
      display: this.$el,
      drawing: true,
      hover: true,
      middle_click_pan: true,
      spacebar_pan: true,
      mousewheel_zoom: true,
      plusminus_zoom: true,
      pinch_zoom: true,
      doubleclick_reset_zoom: true,
      bracket_thickness: true,
    });

    // MaskingModule.on() actually applies events to window.MaskingModule
    MaskingModule
      .on(MaskingModule.Events.LayerClicked, async (e, layerID) => {
        if (!this.interactive) return;

        // set new currentLayer globally
        const shellLayer = this.getShellLayerByID(layerID);
        this.setCurrentLayer(shellLayer);

        // update layer and tool visibility
        this.interactWithLayers();
        await MaskingModule.draw();
      })
      .on(MaskingModule.Events.LayerPerspectiveUpdated, (e, layer) => {
        if (!this.interactive) return;

        // save changes to CF model
        this.saveLayer(layer.id);
      });
  },
};
</script>

<style lang="scss">
.masking-module {
  width: 100%;
  height: 100%;
  overflow: hidden;
}
</style>



// WEBPACK FOOTER //
// src/components/masking/Module.vue